import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AddressManager from '../components/AddressManager';
import PincodeCheck from '../components/PincodeCheck';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Trash2, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSiteName } from '../contexts/SiteSettingsContext';
import { useAffiliate } from '../contexts/AffiliateContext';

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [serviceability, setServiceability] = useState(null);
  const [recs, setRecs] = useState({ enabled: false, heading: '', subheading: '', target_qty: 5, cta_template: '', products: [] });
  const { user } = useAuth();
  const siteName = useSiteName();
  const { getAttributionSnapshot, bindEmail } = useAffiliate();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState('');
  const [deliveryConfig, setDeliveryConfig] = useState({ free_threshold: 500, charge: 60 });
  const [checkoutConfig, setCheckoutConfig] = useState({ gst_rate: 5, cod_enabled: false, cod_max_amount: 5000, cod_convenience_fee: 30 });
  const [codLoading, setCodLoading] = useState(false);

  useEffect(() => { setLoading(true); fetchCart();
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/delivery/config`).then(r => setDeliveryConfig(r.data)).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/config`).then(r => setCheckoutConfig(r.data)).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cart/recommendations`).then(r => setRecs(r.data)).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/addresses`, { withCredentials: true }).then(r => setAddresses(r.data || [])).catch(() => {});
    if (user?.email) bindEmail(user.email);
  }, [user, bindEmail]);

  const selectedPincode = React.useMemo(() => {
    const a = addresses.find(x => x.address_id === selectedAddress);
    return a?.pincode || '';
  }, [addresses, selectedAddress]);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, { withCredentials: true });
      setCartItems(data);
    } catch { toast.error('Failed to load cart'); }
    finally { setLoading(false); }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    const total = cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/validate`, {
        code: couponCode, cart_total: total,
        product_ids: cartItems.map(i => i.product_id),
        categories: cartItems.map(i => i.product?.category).filter(Boolean),
      }, { withCredentials: true });
      setCouponDiscount(data.discount);
      setCouponApplied(data.code);
      toast.success(`Coupon applied! ₹${data.discount} off`);
    } catch (err) {
      setCouponDiscount(0);
      setCouponApplied('');
      toast.error(err.response?.data?.detail || 'Invalid coupon');
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponDiscount(0);
    setCouponApplied('');
  };

  // Re-validate the applied coupon whenever the cart contents change
  // (add / remove / qty change). Keeps the discount in sync with the
  // current basket; if the coupon is no longer valid for the new total,
  // it's removed gracefully and the user is informed.
  React.useEffect(() => {
    if (!couponApplied) return;
    const total = cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);
    if (total <= 0) {
      // Cart emptied — drop the coupon silently.
      setCouponDiscount(0);
      setCouponApplied('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/validate`, {
          code: couponApplied, cart_total: total,
          product_ids: cartItems.map((i) => i.product_id),
          categories: cartItems.map((i) => i.product?.category).filter(Boolean),
        }, { withCredentials: true });
        if (!cancelled && Number(data.discount) !== Number(couponDiscount)) {
          setCouponDiscount(data.discount);
        }
      } catch (err) {
        if (!cancelled) {
          setCouponDiscount(0);
          setCouponApplied('');
          toast.error(err.response?.data?.detail || 'Coupon no longer valid for the updated cart');
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems]);

  const removeFromCart = async (productId, variantKey = '') => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/cart/${productId}`, { params: { variant_key: variantKey }, withCredentials: true });
      toast.success('Removed from cart');
      fetchCart();
    } catch { toast.error('Failed to remove item'); }
  };

  const updateQty = async (productId, newQty, variantKey = '') => {
    if (newQty < 0) return;
    // Optimistic update so UI feels snappy — match by composite key.
    setCartItems((items) => items
      .map((it) => (it.product_id === productId && (it.variant_key || '') === variantKey ? { ...it, quantity: newQty } : it))
      .filter((it) => !(newQty === 0 && it.product_id === productId && (it.variant_key || '') === variantKey)));
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/cart/${productId}`, { quantity: newQty, variant_key: variantKey }, { withCredentials: true });
      fetchCart();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update quantity');
      fetchCart();
    }
  };

  const proceedToCheckout = async () => {
    if (!selectedAddress) {
      toast.error('Please add or select a delivery address');
      return;
    }
    try {
      setLoading(true);
      const { data: orderData } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/checkout/create-order`,
        { address_id: selectedAddress, coupon_code: couponApplied || null, ...getAttributionSnapshot() },
        { withCredentials: true }
      );

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const options = {
          key: orderData.key_id,
          amount: orderData.amount,
          currency: orderData.currency,
          name: siteName,
          description: 'Premium Artisan Tea',
          order_id: orderData.order_id,
          handler: async function (response) {
            try {
              await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/checkout/verify-payment`,
                { razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature },
                { withCredentials: true }
              );
              toast.success('Payment successful!');
              navigate('/orders');
            } catch { toast.error('Payment verification failed'); }
          },
          prefill: { name: user?.name || '', email: user?.email || '', contact: '' },
          theme: { color: '#2E7D32' },
          modal: { ondismiss: () => setLoading(false) }
        };
        const rz = new window.Razorpay(options);
        rz.on('payment.failed', (response) => { toast.error('Payment failed: ' + response.error.description); setLoading(false); });
        rz.open();
        setLoading(false);
      };
      script.onerror = () => { toast.error('Failed to load payment gateway'); setLoading(false); };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate checkout');
      setLoading(false);
    }
  };

  const totalQty = cartItems.reduce((s, it) => s + (it.quantity || 0), 0);

  const computeLine = (item) => {
    const qty = item.quantity || 0;
    // Variants may carry their own price; fall back to product price.
    const price = (item.variant && typeof item.variant.price === 'number' ? item.variant.price : item.product?.price) || 0;
    const tiers = (item.product?.bulk_tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty);
    const applicable = tiers.filter((t) => qty >= t.min_qty);
    const linePct = applicable.length ? Math.max(...applicable.map((t) => t.discount_percent)) : 0;
    // Mutual exclusivity: if a coupon is applied, skip product/basket discounts.
    if (couponApplied) {
      const gross = +(price * qty).toFixed(2);
      return { qty, price, pct: 0, linePct: 0, basketPct: 0, basketUnlocked: false, gross, discount: 0, net: gross, upcoming: tiers.find((t) => t.min_qty > qty) };
    }
    // New tiered basket promo (preferred). Falls back to legacy single-tier.
    const basketTiers = Array.isArray(recs.basket_tiers) ? recs.basket_tiers : [];
    let basketPct = 0;
    if (basketTiers.length > 0) {
      const apl = basketTiers.filter((t) => totalQty >= Number(t.min_qty || 0));
      basketPct = apl.length ? Math.max(...apl.map((t) => Number(t.discount_percent || 0))) : 0;
    } else {
      const legacyPct = Number(recs.basket_discount_percent || 0);
      const legacyMin = Number(recs.basket_min_qty || 0);
      const unlocked = !!recs.enabled && legacyPct > 0 && legacyMin > 0 && totalQty >= legacyMin;
      basketPct = unlocked ? legacyPct : 0;
    }
    const pct = Math.max(linePct, basketPct);
    const upcoming = tiers.find((t) => t.min_qty > qty);
    const gross = +(price * qty).toFixed(2);
    const discount = +(gross * pct / 100).toFixed(2);
    return { qty, price, pct, linePct, basketPct, basketUnlocked: basketPct > 0, gross, discount, net: +(gross - discount).toFixed(2), upcoming };
  };

  const totalAmount = cartItems.reduce((sum, item) => sum + computeLine(item).net, 0);
  const grossAmount = cartItems.reduce((sum, item) => sum + computeLine(item).gross, 0);
  const bulkSavings = +(grossAmount - totalAmount).toFixed(2);
  // Active basket-tier % (0 when a coupon is applied or threshold not met) — for the "Basket discount (X%)" label.
  const activeBasketPct = (() => {
    if (couponApplied) return 0;
    const totalQty = cartItems.reduce((s, it) => s + (it.quantity || 0), 0);
    const tiers = (recs.basket_tiers && recs.basket_tiers.length > 0)
      ? recs.basket_tiers
      : (Number(recs.basket_discount_percent || 0) > 0 && Number(recs.basket_min_qty || 0) > 0
          ? [{ min_qty: Number(recs.basket_min_qty), discount_percent: Number(recs.basket_discount_percent) }]
          : []);
    const apl = tiers.filter((t) => Number(t.min_qty || 0) <= totalQty);
    return apl.length ? Math.max(...apl.map((t) => Number(t.discount_percent || 0))) : 0;
  })();

  const quickAdd = async (productId) => {
    if (!user) { navigate('/login'); return; }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, { product_id: productId, quantity: 1 }, { withCredentials: true });
      toast.success('Added to cart');
      fetchCart();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
  };

  const cartProductIds = new Set(cartItems.map((it) => it.product_id));
  const recsToShow = (recs.products || []).filter((p) => !cartProductIds.has(p.product_id)).slice(0, 3);
  const basketTargetQty = Number(recs.basket_min_qty || recs.target_qty || 0);
  const showRecs = !!recs.enabled && recsToShow.length > 0 && totalQty < basketTargetQty;
  const recsCta = (() => {
    if (!showRecs) return '';
    const needed = Math.max(0, basketTargetQty - totalQty);
    // Prefer the basket-level promo (now guaranteed to apply once threshold met).
    const basketPct = Number(recs.basket_discount_percent || 0);
    let bestPct = basketPct;
    if (bestPct === 0) {
      // Fallback: probe per-line tiers (legacy promise) so the CTA still shows something.
      cartItems.forEach((it) => {
        (it.product?.bulk_tiers || []).forEach((t) => {
          if (t.min_qty <= basketTargetQty && t.discount_percent > bestPct) bestPct = t.discount_percent;
        });
      });
      if (bestPct === 0) {
        recsToShow.forEach((p) => {
          (p.bulk_tiers || []).forEach((t) => {
            if (t.min_qty <= basketTargetQty && t.discount_percent > bestPct) bestPct = t.discount_percent;
          });
        });
      }
    }
    const projectedGross = grossAmount + recsToShow.slice(0, needed).reduce((s, p) => s + p.price, 0);
    const savings = Math.round(projectedGross * bestPct / 100);
    return (recs.cta_template || '').replace('{needed}', needed).replace('{pct}', bestPct).replace('{savings}', savings);
  })();

  // GST-inclusive: prices already include GST; show the GST portion as a breakup.
  const gstRate = Number(checkoutConfig.gst_rate || 0);
  const netAfterDiscount = Math.max(0, totalAmount - couponDiscount);
  const gstAmount = gstRate > 0 ? +(netAfterDiscount - (netAfterDiscount * 100) / (100 + gstRate)).toFixed(2) : 0;
  const codEligible = !!checkoutConfig.cod_enabled && (!checkoutConfig.cod_max_amount || netAfterDiscount <= checkoutConfig.cod_max_amount);
  const codConvenienceFee = codEligible ? Number(checkoutConfig.cod_convenience_fee || 0) : 0;

  const payCOD = async () => {
    if (!selectedAddress) { toast.error('Please add or select a delivery address'); return; }
    setCodLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/checkout/cod/create-order`,
        { address_id: selectedAddress, coupon_code: couponApplied || null, ...getAttributionSnapshot() },
        { withCredentials: true }
      );
      toast.success(`COD order placed: ${data.order_id}`);
      navigate('/orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place COD order');
    } finally { setCodLoading(false); }
  };

  if (loading && cartItems.length === 0) {
    return (<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>);
  }

  return (
    <div className="min-h-screen">
      <Header />
      <div className="container mx-auto px-4 py-12 pt-20">
        <h1 className="text-4xl font-black font-heading mb-8" data-testid="cart-heading">Shopping Cart</h1>

        {cartItems.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-4" data-testid="empty-cart-message">Your cart is empty</p>
            <Button onClick={() => navigate('/products')} className="rounded-full" data-testid="continue-shopping-button">Browse Teas</Button>
          </div>
        ) : (
          <>
            <div className="mb-6 rounded-xl border-2 border-red-500/60 bg-red-50 px-4 py-3 text-sm" data-testid="discount-rules-banner">
              <p className="font-bold text-red-700 mb-1">Discount rules</p>
              <ul className="text-red-700 list-disc pl-5 space-y-0.5">
                <li>Buy <b>2+</b> products → automatic <b>5% off</b>.</li>
                <li>Buy <b>3+</b> products → automatic <b>10% off</b>.</li>
                <li>If you apply a valid <b>coupon</b>, the product/basket discount will <b>not</b> apply (only one discount per order).</li>
              </ul>
            </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <React.Fragment key={item.cart_item_id}>
                <div className="flex gap-4 p-5 rounded-2xl bg-white border border-[#E0E0E0]" data-testid={`cart-item-${item.product_id}`}>
                  <img src={item.product?.image_url} alt={item.product?.name} className="w-20 h-20 object-cover rounded-lg" />
                  <div className="flex-1">
                    <h3 className="font-bold font-heading" data-testid={`cart-item-name-${item.product_id}`}>{item.product?.name}</h3>
                    {item.variant && Object.keys(item.variant).length > 0 && (
                      <p className="text-[11px] uppercase tracking-wide font-bold text-muted-foreground" data-testid={`cart-item-variant-${item.product_id}`}>
                        {Object.entries(item.variant).filter(([k]) => k !== 'price').map(([k, v]) => `${k}: ${v}`).join(' · ')}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mb-2">{'\u20B9'}{(item.variant?.price || item.product?.price || 0).toFixed(2)} each</p>
                    <div className="inline-flex items-center rounded-full border border-[#E0E0E0] bg-[#FAFAFA]">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateQty(item.product_id, item.quantity - 1, item.variant_key || '')}
                        disabled={item.quantity <= 1}
                        data-testid={`cart-qty-decrement-${item.product_id}`}
                        aria-label="Decrease quantity"
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center text-sm font-medium" data-testid={`cart-qty-value-${item.product_id}`}>{item.quantity}</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateQty(item.product_id, item.quantity + 1, item.variant_key || '')}
                        disabled={item.product?.stock !== undefined && item.quantity >= item.product.stock}
                        data-testid={`cart-qty-increment-${item.product_id}`}
                        aria-label="Increase quantity"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    {item.product?.stock <= 5 && <p className="text-xs text-accent font-medium mt-1">Only {item.product.stock} left!</p>}
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <div className="text-right">
                      {(() => { const line = computeLine(item); return line.discount > 0 ? (
                        <>
                          <span className="text-xs line-through text-muted-foreground">{'\u20B9'}{line.gross.toFixed(2)}</span>
                          <span className="block text-lg font-bold text-primary" data-testid={`cart-item-price-${item.product_id}`}>{'\u20B9'}{line.net.toFixed(2)}</span>
                          <span className="block text-[11px] text-primary font-medium" data-testid={`cart-item-tier-savings-${item.product_id}`}>{line.pct}% off · saved ₹{line.discount.toFixed(0)}</span>
                        </>
                      ) : (
                        <span className="text-lg font-bold text-primary" data-testid={`cart-item-price-${item.product_id}`}>{'\u20B9'}{line.net.toFixed(2)}</span>
                      ); })()}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeFromCart(item.product_id, item.variant_key || '')} data-testid={`remove-cart-item-${item.product_id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                {(() => { const line = computeLine(item); return line.upcoming ? (
                  <p className="text-xs text-accent -mt-2 mb-2 px-1" data-testid={`cart-item-tier-upsell-${item.product_id}`}>
                    Add {line.upcoming.min_qty - item.quantity} more to save ₹{((item.product.price * line.upcoming.min_qty * line.upcoming.discount_percent) / 100).toFixed(0)} ({line.upcoming.discount_percent}% off)
                  </p>
                ) : null; })()}
              </React.Fragment>
              ))}

              {showRecs && (
                <div className="p-5 rounded-2xl bg-gradient-to-br from-primary/5 via-white to-accent/5 border border-primary/15" data-testid="cart-recommendations-shelf">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-heading font-bold text-base" data-testid="recs-heading">{recs.heading}</h3>
                      <p className="text-xs text-muted-foreground" data-testid="recs-subheading">{recs.subheading}</p>
                    </div>
                    {recsCta && (
                      <span className="text-xs font-semibold text-accent text-right max-w-[40%]" data-testid="recs-cta">{recsCta}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {recsToShow.map((p) => (
                      <div key={p.product_id} className="p-3 rounded-xl bg-white border border-[#E0E0E0] flex flex-col" data-testid={`rec-card-${p.product_id}`}>
                        <Link to={`/products/${p.product_id}`} className="block">
                          <img src={p.image_url} alt={p.name} className="w-full h-20 object-cover rounded-md mb-2" />
                          <p className="text-xs font-semibold line-clamp-2" data-testid={`rec-name-${p.product_id}`}>{p.name}</p>
                        </Link>
                        <p className="text-sm font-bold text-primary mt-1">{'\u20B9'}{p.price}</p>
                        <Button size="sm" variant="outline" className="mt-2 rounded-full text-xs h-8" onClick={() => quickAdd(p.product_id)} data-testid={`rec-add-${p.product_id}`}>
                          + Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-1 space-y-4">
              {/* Address Selection */}
              <div className="p-5 rounded-2xl bg-white border border-[#E0E0E0]">
                <AddressManager onSelect={setSelectedAddress} selectedId={selectedAddress} />
              </div>

              {/* Pincode Serviceability Check */}
              <div className="p-5 rounded-2xl bg-white border border-[#E0E0E0]" data-testid="pincode-serviceability-box">
                <h3 className="font-heading font-semibold text-sm mb-3">Delivery Serviceability</h3>
                <PincodeCheck
                  pincode={selectedPincode}
                  onResult={setServiceability}
                  testIdPrefix="cart-pincode"
                />
              </div>

              {/* Coupon */}
              <div className="p-5 rounded-2xl bg-white border border-[#E0E0E0]">
                <h3 className="font-heading font-semibold text-sm mb-2">Coupon Code</h3>
                {couponApplied ? (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                    <div>
                      <span className="font-mono font-bold text-primary text-sm">{couponApplied}</span>
                      <p className="text-xs text-green-700">₹{couponDiscount.toFixed(2)} off applied</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-xs text-destructive" onClick={removeCoupon} data-testid="remove-coupon-button">Remove</Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="cart-coupon-input" />
                    <Button variant="outline" size="sm" onClick={applyCoupon} data-testid="apply-coupon-button">Apply</Button>
                  </div>
                )}
              </div>

              {/* Order Summary */}
              <div className="p-6 rounded-2xl bg-white border border-[#E0E0E0] sticky top-24">
                <h2 className="text-lg font-bold font-heading mb-4">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span data-testid="cart-subtotal">{'\u20B9'}{grossAmount.toFixed(2)}</span>
                  </div>
                  {bulkSavings > 0 && (
                    <div className="flex justify-between text-sm text-primary" data-testid="cart-bulk-savings-row">
                      <span>{activeBasketPct > 0 ? `Basket discount (${activeBasketPct}%)` : 'Bulk savings'}</span>
                      <span data-testid="cart-bulk-savings">-{'\u20B9'}{bulkSavings.toFixed(2)}</span>
                    </div>
                  )}
                  {couponApplied && (
                    <p className="text-xs text-muted-foreground italic -mt-1" data-testid="cart-coupon-note">
                      Coupon applied — automatic basket discount waived (only one discount per order).
                    </p>
                  )}
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount ({couponApplied})</span>
                      <span data-testid="cart-discount">-{'\u20B9'}{couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {gstRate > 0 && (
                    <div className="flex justify-between text-xs text-muted-foreground" data-testid="cart-gst-row">
                      <span>Incl. GST @{gstRate}%</span>
                      <span data-testid="cart-gst-amount">{'\u20B9'}{gstAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="text-muted-foreground italic" data-testid="cart-shipping">Calculated at checkout</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-primary" data-testid="cart-total">{'\u20B9'}{netAfterDiscount.toFixed(2)}</span>
                  </div>
                  {totalAmount < deliveryConfig.free_threshold && <p className="text-xs text-muted-foreground">Add {'\u20B9'}{(deliveryConfig.free_threshold - totalAmount).toFixed(0)} more for free shipping</p>}
                </div>

                <Button className="w-full rounded-full" size="lg" onClick={proceedToCheckout} disabled={loading || !selectedAddress || serviceability === null || !serviceability.serviceable} data-testid="checkout-button">
                  {loading ? 'Processing...' : !selectedAddress ? 'Add Address First' : serviceability === null ? 'Check pincode to continue' : !serviceability.serviceable ? 'Pincode not serviceable' : 'Pay with Razorpay'}
                </Button>

                {checkoutConfig.cod_enabled && (
                  <>
                    <div className="relative my-3">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#E0E0E0]" /></div>
                      <div className="relative flex justify-center text-[10px] uppercase tracking-wider"><span className="bg-white px-2 text-muted-foreground">or</span></div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full rounded-full"
                      size="lg"
                      onClick={payCOD}
                      disabled={codLoading || !selectedAddress || !codEligible || serviceability === null || !serviceability.serviceable}
                      data-testid="cod-checkout-button"
                    >
                      {codLoading ? 'Placing order...'
                        : serviceability === null ? 'Check pincode to continue'
                        : !serviceability.serviceable ? 'Pincode not serviceable'
                        : !codEligible && checkoutConfig.cod_max_amount ? `COD unavailable above \u20B9${checkoutConfig.cod_max_amount}`
                        : `Cash on Delivery${codConvenienceFee > 0 ? ` (+\u20B9${codConvenienceFee} fee)` : ''}`}
                    </Button>
                    {codEligible && codConvenienceFee > 0 && (
                      <p className="text-[11px] text-center text-muted-foreground mt-1.5" data-testid="cod-fee-note">
                        Total on delivery: {'\u20B9'}{(netAfterDiscount + codConvenienceFee).toFixed(2)}
                      </p>
                    )}
                  </>
                )}

                <p className="text-xs text-center text-muted-foreground mt-3">Secure payment. All prices in INR.</p>
              </div>
            </div>
          </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CartPage;
